import { Box, IconButton, ListItemIcon, ListItemText, Menu, MenuItem } from "@mui/material"
import { CheckOutlined, CloseOutlined, EditOutlined, MoreVertOutlined } from "@mui-symbols-material/w300"
import type { CellContext } from "@tanstack/react-table"
import type { FC } from "react"
import { useRef, useState } from "react"

import type { ProductTestResult } from "@/graphql/codegen/graphql"
import { withClose } from "@/utils/withClose"

interface ProductTestResultRowActionsProps {
  row: CellContext<ProductTestResult, unknown>["row"]
  canOverride?: boolean
  canManageApproval?: boolean
  onOverride?: (productTestResult: ProductTestResult) => void
  onApprovalRequest?: (productTestResult: ProductTestResult) => void
  onApprovalCancel?: (productTestResult: ProductTestResult) => void
}

const ProductTestResultRowActions: FC<ProductTestResultRowActionsProps> = ({
  row,
  canOverride,
  canManageApproval,
  onOverride,
  onApprovalRequest,
  onApprovalCancel,
}) => {
  const anchorElRef = useRef<HTMLButtonElement>(null)
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)

  const handleDropdownOpen = () => setIsDropdownOpen(true)
  const handleDropdownClose = () => setIsDropdownOpen(false)

  const handleOverride = withClose(handleDropdownClose)(() => onOverride?.(row.original))
  const handleApprovalRequest = withClose(handleDropdownClose)(() => onApprovalRequest?.(row.original))
  const handleApprovalCancel = withClose(handleDropdownClose)(() => onApprovalCancel?.(row.original))

  const hasPendingApproval = Boolean(row.original.qualityApproval)

  return (
    <Box ref={anchorElRef}>
      <IconButton size='small' onClick={handleDropdownOpen} data-test='expanded-row-actions-button'>
        <MoreVertOutlined />
      </IconButton>
      <Menu
        anchorEl={anchorElRef.current}
        open={isDropdownOpen}
        onClose={handleDropdownClose}
        data-test='expanded-row-actions-menu'
      >
        {canOverride && (
          <MenuItem data-test='override-result-menu-item' onClick={handleOverride}>
            <ListItemIcon>
              <EditOutlined />
            </ListItemIcon>
            <ListItemText primary='Override Result' />
          </MenuItem>
        )}
        {canManageApproval && !hasPendingApproval && (
          <MenuItem data-test='request-approval-menu-item' onClick={handleApprovalRequest}>
            <ListItemIcon>
              <CheckOutlined />
            </ListItemIcon>
            <ListItemText primary='Request Out of Spec Approval' />
          </MenuItem>
        )}
        {canManageApproval && hasPendingApproval && (
          <MenuItem data-test='cancel-approval-menu-item' onClick={handleApprovalCancel}>
            <ListItemIcon>
              <CloseOutlined />
            </ListItemIcon>
            <ListItemText primary='Cancel Out of Spec Approval' />
          </MenuItem>
        )}
      </Menu>
    </Box>
  )
}

export default ProductTestResultRowActions
